"use client";

import { useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "@/i18n/navigation";
import { artistLinkRel } from "@/lib/seo-enrichment";
import Image from "next/image";
import { Plus, Check, X } from "lucide-react";
import { FestivalAttraction, isActivity } from "@/lib/types";
import { formatTime, isOwnImage } from "@/lib/utils";
import { useSpotifyPlayer } from "../../spotify/spotify-player-context";
import { ArtistExternalLinks } from "../../artist-external-links";
import { SubscribeButton } from "../../subscribe-button";
import { useScheduleBuilder } from "./schedule-context";
import type { ScheduleSlot } from "./use-schedule-data";
import { PlayIcon } from "@/components/spotify/play-icon";

interface PerformanceModalProps {
  slot: ScheduleSlot;
  festivalId: number;
  eventId: number;
  blockedSpotifyIds: string[];
  onClose: () => void;
}

export function PerformanceModal({
  slot,
  festivalId,
  eventId,
  blockedSpotifyIds,
  onClose,
}: PerformanceModalProps) {
  const { play, artistId: activeArtistId, setJukeboxOpen } = useSpotifyPlayer();
  const { savedSetKeys, toggleScheduleItem } = useScheduleBuilder();

  const artists = slot.attractions.filter(
    (a): a is FestivalAttraction => !isActivity(a)
  );
  const primary = artists[0];
  const hasPrimary = primary != null;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    setJukeboxOpen(true);
    return () => {
      document.body.style.overflow = "";
      setJukeboxOpen(false);
    };
  }, [setJukeboxOpen]);

  if (!hasPrimary) return null;

  const primaryImage =
    primary.image_url && isOwnImage(primary.image_url)
      ? primary.image_url
      : null;
  const displayGenres = [...new Set(primary.genres.filter(Boolean))].slice(
    0,
    3
  );
  const stage = primary.stage;
  const isSaved = savedSetKeys.has(`${eventId}-${primary.id}`);

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-4 pb-[210px]"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm overflow-hidden border border-neutral-800 bg-surface"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Corner markers */}
        <div className="absolute -left-1 -top-1 h-3 w-3 border-l border-t border-acid" />
        <div className="absolute -right-1 -top-1 h-3 w-3 border-r border-t border-acid" />
        <div className="absolute -bottom-1 -left-1 h-3 w-3 border-b border-l border-acid" />
        <div className="absolute -bottom-1 -right-1 h-3 w-3 border-b border-r border-acid" />

        {/* Hero image */}
        <div className="relative aspect-[3/2] w-full overflow-hidden">
          {primaryImage ? (
            <>
              <Image
                src={primaryImage}
                alt={primary.name}
                fill
                className="object-cover"
                sizes="400px"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
            </>
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-neutral-900">
              <span className="font-mono text-5xl text-neutral-700">
                {primary.name.charAt(0).toUpperCase()}
              </span>
            </div>
          )}

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full bg-black/50 text-neutral-400 backdrop-blur-sm transition-colors hover:text-white"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path
                d="M4 4L12 12M12 4L4 12"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>

          {/* Name + genres overlay */}
          <div className="absolute inset-x-0 bottom-0 px-5 pb-4">
            <Link
              href={
                primary.child_event
                  ? `/${primary.child_event.city_slug}/event/${primary.child_event.slug}`
                  : `/artist/${primary.slug}`
              }
              rel={primary.child_event ? "nofollow" : artistLinkRel(primary)} prefetch={false}
              className="font-mono text-xl font-bold uppercase tracking-tight text-white transition-colors hover:text-acid drop-shadow-lg line-clamp-2"
            >
              {primary.name}
            </Link>
            {displayGenres.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {displayGenres.map((g) => (
                  <span
                    key={g}
                    className="rounded-full border border-white/20 bg-black/30 px-2 py-0.5 font-mono text-[9px] text-white/70 backdrop-blur-sm"
                  >
                    {g.toUpperCase()}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Details */}
        <div className="px-5 py-4 space-y-3">
          {/* Time + Stage */}
          <div className="flex items-center gap-3 font-mono text-xs text-neutral-400">
            {primary.set_time && (
              <span>
                {formatTime(primary.set_time)}
                {primary.set_end_time && (
                  <span className="text-neutral-600">
                    {" "}
                    – {formatTime(primary.set_end_time)}
                  </span>
                )}
              </span>
            )}
            {stage && (
              <>
                <span className="text-neutral-700">·</span>
                <span className="text-neutral-500">{stage}</span>
              </>
            )}
          </div>

          {/* External links for primary */}
          {primary.external_links &&
            Object.keys(primary.external_links).length > 0 && (
              <ArtistExternalLinks links={primary.external_links} size="sm" />
            )}

          {/* Spotify + Subscribe for primary */}
          <div className="flex items-center gap-2">
            {primary.spotify_id &&
              !blockedSpotifyIds.includes(primary.spotify_id) && (
                <button
                  onClick={() =>
                    play(primary.spotify_id as string, primary.name)
                  }
                  className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 font-mono text-[10px] font-bold tracking-wider transition-all cursor-pointer ${
                    activeArtistId === primary.spotify_id
                      ? "border-[#1DB954]/50 bg-[#1DB954]/10 text-[#1DB954]"
                      : "border-neutral-700 text-neutral-400 hover:border-[#1DB954]/50 hover:text-[#1DB954]"
                  }`}
                >
                  <PlayIcon className="h-3.5 w-3.5" />
                  {activeArtistId === primary.spotify_id
                    ? "PLAYING"
                    : "PREVIEW"}
                </button>
              )}
            <SubscribeButton
              type="artist"
              targetId={primary.id}
              targetName={primary.name}
              targetImage={primary.image_url}
              size="sm"
            />
          </div>

          {/* Additional artists in this slot */}
          {artists.length > 1 && (
            <div className="border-t border-neutral-800 pt-3">
              <p className="mb-2 font-mono text-[10px] font-bold tracking-wider text-neutral-500">
                ALSO PERFORMING
              </p>
              <div className="space-y-2">
                {artists.slice(1).map((a) => {
                  const hasSpotify =
                    a.spotify_id &&
                    !blockedSpotifyIds.includes(a.spotify_id);
                  const isPlaying = hasSpotify && a.spotify_id === activeArtistId;
                  const aImage = a.image_url && isOwnImage(a.image_url);
                  return (
                    <div key={a.id} className="flex items-center gap-2.5">
                      <Link
                        href={
                          a.child_event
                            ? `/${a.child_event.city_slug}/event/${a.child_event.slug}`
                            : `/artist/${a.slug}`
                        }
                        rel={a.child_event ? "nofollow" : artistLinkRel(a)} prefetch={false}
                        className="relative h-8 w-8 shrink-0 overflow-hidden rounded-full border border-neutral-700"
                      >
                        {aImage ? (
                          <Image
                            src={a.image_url!}
                            alt={a.name}
                            fill
                            className="object-cover"
                            sizes="32px"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center bg-neutral-800 font-mono text-[10px] text-neutral-400">
                            {a.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </Link>
                      <Link
                        href={`/artist/${a.slug}`}
                        rel={artistLinkRel(a)} prefetch={false}
                        className="min-w-0 flex-1 truncate font-mono text-xs text-neutral-300 transition-colors hover:text-acid"
                      >
                        {a.name}
                      </Link>
                      {hasSpotify && (
                        <button
                          onClick={() =>
                            play(a.spotify_id as string, a.name)
                          }
                          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-colors cursor-pointer ${
                            isPlaying
                              ? "text-[#1DB954]"
                              : "text-neutral-600 hover:text-[#1DB954]"
                          }`}
                        >
                          <PlayIcon className="h-3 w-3" />
                        </button>
                      )}
                      <SubscribeButton
                        type="artist"
                        targetId={a.id}
                        targetName={a.name}
                        targetImage={a.image_url}
                        size="sm"
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Add to schedule */}
          <button
            onClick={() =>
              toggleScheduleItem(
                festivalId,
                eventId,
                primary.id,
                primary.name
              )
            }
            className={`flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg border py-3 font-mono text-sm font-bold tracking-wider transition-all ${
              isSaved
                ? "border-acid/30 bg-acid/10 text-acid hover:border-red-500/30 hover:bg-red-500/10 hover:text-red-400 group"
                : "border-neutral-700 text-neutral-400 hover:border-acid/50 hover:text-acid"
            }`}
          >
            {isSaved ? (
              <>
                <Check className="h-4 w-4 group-hover:hidden" />
                <X className="h-4 w-4 hidden group-hover:block" />
                <span className="group-hover:hidden">IN MY SCHEDULE</span>
                <span className="hidden group-hover:inline">REMOVE</span>
              </>
            ) : (
              <>
                <Plus className="h-4 w-4" />
                ADD TO SCHEDULE
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
